import { type Transaction } from '@kalos/kalos-rpc';
import {
  Button,
  Combobox,
  DateInput,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SimpleSelect,
  Textarea,
} from '@kalos/ui';
import { format } from 'date-fns';
import { type FC } from 'react';

import { JobSelector } from '../../../components/JobSelector';
import { TransactionAccountComboboxPicker } from '../Pickers/newPickers/QueryPickerV2';
import { FormAlertArea } from '../TransactionTable/components/FormAlertArea';
import { VendorField } from '../TransactionTable/components/VendorField';
import {
  getEditTransactionMaxDate,
  tagsTranslated,
  useEditTransactionForm,
} from './editTransactionForm';

interface Props {
  transactionInput: Transaction;
  onSave: (saved: Transaction) => void;
  onClose: () => void;
  title?: string;
  disabled?: boolean;
  isLoading?: boolean;
  changeCreator?: boolean;
  isAccountsPayableAdmin?: boolean;
}

export const EditTransaction: FC<Props> = ({
  transactionInput,
  disabled,
  onSave,
  onClose,
  changeCreator,
  isAccountsPayableAdmin,
  isLoading,
  title,
}) => {
  const {
    form,
    jobId,
    jobSelectorHint,
    timesheetDepartmentQuery,
    techniciansQuery,
    formAlert,
    handleCheckInvoiceNumber,
    handleSave,
    handleSetDepartment,
  } = useEditTransactionForm({ transactionInput, disabled, onSave });

  return (
    <FormContext {...form}>
      <form
        onSubmit={form.handleSubmit(handleSave)}
        className="grid max-h-screen grid-cols-1 gap-4 overflow-auto p-4 sm:grid-cols-2"
      >
        <h3 className="text-xl font-bold">{title}</h3>
        <div className="flex gap-4 place-self-end">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            disabled={!form.formState.isValid || !form.formState.isDirty}
          >
            Save
          </Button>
        </div>
        <FormField
          control={form.control}
          name="jobId"
          render={({ field: { value, onChange, disabled } }) => (
            <FormItem>
              <FormLabel>Job #</FormLabel>
              <FormMessage />
              <FormControl>
                <JobSelector
                  hint={jobSelectorHint}
                  disabled={disabled}
                  value={value}
                  onChange={(val) => {
                    onChange(val);
                    handleSetDepartment(val);
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="departmentId"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Department</FormLabel>
                <FormMessage />
                <SimpleSelect
                  disabled={
                    !timesheetDepartmentQuery.isSuccess ||
                    field.disabled ||
                    (!isAccountsPayableAdmin && !!jobId)
                  }
                  placeholder={'Select Department'}
                  selectedValue={field.value.toString()}
                  onChange={(value) => {
                    if (value) field.onChange(value);
                  }}
                  values={timesheetDepartmentQuery.data ?? []}
                />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="ownerId"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Purchaser/PM</FormLabel>
                <FormMessage />
                <Combobox
                  onChange={field.onChange}
                  value={field.value?.toString() ?? ''}
                  emptyLabel={'Select Purchaser/PM'}
                  disabled={techniciansQuery.isPending || field.disabled}
                  values={techniciansQuery.data ?? []}
                  placeholder="Search Purchaser/PM"
                />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="costCenterId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cost Center</FormLabel>
              <FormMessage />
              <FormControl>
                <TransactionAccountComboboxPicker
                  disabled={field.disabled}
                  selected={field.value.toString()}
                  onSelect={(tA) => field.onChange(tA?.id ?? 0)}
                  renderItem={(tA) => `${tA.id}-${tA.description}`}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex items-center gap-4 ">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Amount</FormLabel>
                <FormMessage />
                <FormControl>
                  <Input type="number" step="0.01" inputMode="numeric" {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="assignedEmployeeId"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Creator</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Combobox
                      onChange={field.onChange}
                      value={field.value?.toString() ?? ''}
                      emptyLabel={'Select purchaser'}
                      disabled={techniciansQuery.isPending || !changeCreator || field.disabled}
                      values={techniciansQuery.data ?? []}
                      placeholder="Search Purchaser"
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />
        </div>

        <FormField
          control={form.control}
          name="vendorCategory"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Vendor Category</FormLabel>
                <FormMessage />
                <FormControl>
                  <SimpleSelect
                    placeholder={'Select Vendor Category'}
                    selectedValue={field.value?.toString() ?? '0'}
                    disabled={field.disabled}
                    onChange={field.onChange}
                    values={tagsTranslated}
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="timestamp"
          render={({ field: { ref, ...field } }) => {
            const maxDate = getEditTransactionMaxDate();
            return (
              <FormItem>
                <FormLabel>Timestamp</FormLabel>
                <FormMessage />
                <FormControl>
                  <DateInput {...field} maxDate={maxDate} />
                </FormControl>
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="paymentDueDate"
          render={({ field }) => {
            const paymentDueDate = field.value ? new Date(field.value) : new Date();
            const maxDate = getEditTransactionMaxDate();
            return (
              <FormItem>
                <FormLabel>Payment Due Date</FormLabel>
                <FormMessage />
                <FormControl>
                  <DateInput
                    value={paymentDueDate}
                    onChange={(date) => {
                      const dateString = date ? format(date, 'yyyy-MM-dd HH:mm:ss') : '';
                      field.onChange(dateString);
                    }}
                    maxDate={maxDate}
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormMessage />
                <FormControl>
                  <Input type="text" {...field} />
                </FormControl>
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => {
            return (
              <FormItem className="col-span-1 sm:col-span-2">
                <FormLabel>Description</FormLabel>
                <FormMessage />
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="vendorId"
          render={({ field }) => (
            <VendorField value={field.value} onChange={field.onChange} disabled={field.disabled} />
          )}
        />

        <div className="flex gap-4">
          <FormField
            control={form.control}
            name="orderNumber"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>Purchase Order #</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Input type="text" {...field} />
                  </FormControl>
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="invoiceNumber"
            render={({ field }) => {
              const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                field.onBlur();
                handleCheckInvoiceNumber(e.target.value);
              };
              return (
                <FormItem>
                  <FormLabel>Invoice #</FormLabel>
                  <FormMessage />
                  <FormControl>
                    <Input type="text" {...field} onBlur={onBlur} />
                  </FormControl>
                </FormItem>
              );
            }}
          />
        </div>

        <FormAlertArea alert={formAlert} />
      </form>
    </FormContext>
  );
};
